import { useEffect } from 'react'
import { useEntitiesClientState } from './'
import { useEnabled, createUseResolveInitialItems } from 'state/utilities'
import { useQueryClient, useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { getPaging, getFilter } from './helpers'
import { get } from 'api'
import isEqual from 'react-fast-compare'
import { omit, pick } from 'utilities/object'
import { pruneBy } from 'utilities/array'
import {
    getGroupsInfiniteQuery,
    getSorting as getGroupsSorting,
    getPaging as getGroupsPaging
} from 'state/groups'

export const getEntitiesInfiniteQuery = (key, params = {}, options = {}) => ({
    ...omit(options, 'key'),
    queryKey: [...key, params]
})

export const useEntities = (options = {}) => {
    const clientState = useEntitiesClientState(state => ({
        queryOptions: state.queryOptions,
        path: state.path,
        filter: state.filter,
        sorting: state.sorting,
        paging: state.paging,
        adaptParams: state.adaptParams,
        adaptItems: state.adaptItems
    }), true)

    const queryOptions = {
        ...clientState.queryOptions,
        ...options?.queryOptions
    }

    const key = [...(queryOptions.key ?? [])]
    const path = options?.path ?? clientState.path
    const adaptParams = options?.adaptParams ?? clientState.adaptParams ?? (params => params)

    const rawParams = {
        ...pick(clientState, 'filter', 'sorting', 'paging'),
        ...pick(options, 'filter', 'sorting', 'paging')
    }

    let params = adaptParams({
        ...clientState.filter,
        ...clientState.paging,
        ...getFilter(rawParams)?.filter,
        ...getPaging(rawParams)?.paging,
        ...options?.params
    })

    let query = getEntitiesInfiniteQuery(key, { ...params, path }, queryOptions)

    if(path === '/groups') {
        params = adaptParams({
            ...clientState.filter,
            ...clientState.paging,
            ...getFilter(rawParams)?.filter,
            ...getGroupsSorting(rawParams)?.sorting,
            ...getGroupsPaging(rawParams)?.paging,
            ...options?.params
        })

        query = getGroupsInfiniteQuery(params, queryOptions)
    }

    const requirements = !!key?.length && !!path
    const [enabled, enable] = useEnabled(queryOptions)
    const queryEnabled = requirements && enabled

    const { data, ...result } = useInfiniteQuery({
        ...query,
        queryFn: async ({ pageParam }) => {
            const { response, ...result } = await get({
                path,
                params: {
                    ...params,
                    offset: pageParam * (params?.limit ?? 0)
                }
            })

            const data = {
                ...result,
                ...response
            }

            if([400, '(Network error)'].includes(result.status)) {
                return Promise.reject(data)
            }

            let items = Array.isArray(response) ?
                response :
                (response?.items ?? [])

            const adaptItems = options?.adaptItems ?? clientState.adaptItems ?? (items => items)
            items = adaptItems(items)

            const total = response?.total ?? items.length

            return {
                ...data,
                items,
                total
            }
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
            const fetchedPageCount = allPages.length
            if(fetchedPageCount >= lastPage.total / (params?.limit ?? 1)) {
                return null
            }

            const fetchedItemCount = itemize(allPages).length
            if(fetchedItemCount >= lastPage.total) {
                return null
            }

            return lastPageParam + 1
        },
        enabled: queryEnabled
    })

    useEffect(() => {
        if(!queryEnabled || !queryOptions.exhaustive) {
            return
        }

        if(result.isFetched && result.hasNextPage && !result.isFetchingNextPage) {
            result.fetchNextPage()
        }
    }, [queryEnabled, queryOptions.exhaustive, result])

    return {
        ...result,
        ...(queryOptions.exhaustive ? {
            isFetched: result.isFetched && !result.hasNextPage && !result.isFetchingNextPage
        } : null),
        data,
        items: itemize(data?.pages),
        total: count(data?.pages),
        enabled: queryEnabled,
        enable
    }
}

export const useEntityCount = (options = {}) => {
    const clientState = useEntitiesClientState(state => ({
        queryOptions: state.queryOptions,
        path: state.path,
        filter: state.filter,
        adaptParams: state.adaptParams
    }), true)

    const queryOptions = {
        ...clientState.queryOptions,
        ...options?.queryOptions
    }

    const key = [...(queryOptions.key ?? [])]
    const path = options?.path ?? clientState.path
    const adaptParams = options?.adaptParams ?? clientState.adaptParams ?? (params => params)

    const params = adaptParams({
        ...clientState.filter,
        ...getFilter(options)?.filter,
        ...options?.params
    })

    const requirements = !!key?.length && !!path
    const [enabled, enable] = useEnabled(queryOptions)
    const queryEnabled = requirements && enabled

    const result = useQuery({
        ...queryOptions,
        queryKey: ['count', ...key, { ...params, path }],
        queryFn: async () => {
            const { response, ...result } = await get({
                path,
                params: {
                    ...params,
                    limit: 0
                }
            })

            const data = {
                ...result,
                ...response
            }

            if(result.status === 400) {
                return Promise.reject(data)
            }

            const total = response?.total ?? (Array.isArray(response) ?
                response :
                (response?.items ?? [])
            ).length ?? 0

            return {
                ...data,
                total
            }
        },
        select: data => data?.total ?? 0,
        enabled: queryEnabled
    })

    return {
        ...result,
        count: result.data ?? 0,
        enabled,
        enable
    }
}

export const useFetchEntities = () => {
    const queryClient = useQueryClient()

    return (options = {}) => {
        const key = [...(options?.queryOptions?.key ?? [])]
        const path = options?.path

        if(!key?.length || !path) {
            return Promise.reject({
                ok: false,
                response: {
                    error: 'Key or path is missing'
                }
            })
        }
        const adaptParams = options?.adaptParams ?? (params => params)

        const params = adaptParams({
            ...getFilter(options)?.filter,
            ...getPaging(options)?.paging,
            ...options?.params
        })

        return queryClient.fetchQuery({
            ...options?.queryOptions,
            queryKey: ['imperative', ...key, { ...params, path }],
            // staleTime: 0 // As it is imperative, there will be no live subscription to new data
            queryFn: async () => {
                const result = await get({ path, params })

                if(result.status === 400) {
                    return Promise.reject(result)
                }

                const adaptItems = options?.adaptItems ?? (items => items)
                const items = adaptItems(result.response?.items ?? [])

                return {
                    ...result,
                    response: {
                        ...result?.response,
                        items
                    }
                }
            }
        })
    }
}

export const useResolveInitialEntities = createUseResolveInitialItems(useEntities)

const itemize = pages => pruneBy(pages?.flatMap(({ items }) => items) ?? [])
const count = pages => pages?.[0]?.total ?? 0

export const useUpdateEntitiesCache = () => {
    const queryClient = useQueryClient()

    return (method, entitiesQueryKey) => {
        if(!method || !queryClient[method] || !entitiesQueryKey?.length) {
            return
        }

        queryClient[method]({
            predicate: ({ queryKey }) => {
                const countQueryKey = ['count', ...entitiesQueryKey]

                const itemsMatch = isEqual(entitiesQueryKey, queryKey.slice(0, entitiesQueryKey.length))
                const countMatch = isEqual(countQueryKey, queryKey.slice(0, countQueryKey.length))

                return itemsMatch || countMatch
            }
        })
    }
}